import { rubricsApi } from '@edulastic/api'
import { questionType } from '@edulastic/constants'
import {
  buildRubricAttachMetadata,
  computeRubricMaxScore,
} from '../../GradingRubric/Components/common/helper'
import {
  isItemClonableByUser,
  isItemEditableByUser,
  isStandardAddedToQuestion,
} from './AIQuestionAssistance'

const { ESSAY_RICH_TEXT, ESSAY_PLAIN_TEXT } = questionType

/**
 * Same access gate as Pear Assist standards: user may edit in place or clone.
 * @param {Object} params
 * @returns {boolean}
 */
const canApplyRubricToItem = ({
  item,
  userId,
  writableCollections = [],
  userCollections = [],
  userRole,
  isCurator,
}) =>
  isItemEditableByUser({
    item,
    userId,
    writableCollections,
    userRole,
    isCurator,
  }) || isItemClonableByUser({ item, userId, userCollections })

/**
 * Essay types eligible for Pear Assist rubric apply.
 * Only types that also support Pear Assist standards generation — Math Essay /
 * Inline Math Essay are not AI-metadata eligible, so they never receive
 * generated standards and must not get Continue rubric apply / list VIEW.
 */
export const PEAR_ASSIST_RUBRIC_ESSAY_TYPES = [
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
]

/**
 * Whether Review should show Pear Assist Rubric VIEW for a suggestion.
 * Requires both Continue apply (`rubricApplied`) and generated standards.
 * @param {Object} [suggestion]
 * @returns {boolean}
 */
export const shouldShowPearAssistRubric = (suggestion = {}) =>
  Boolean(
    suggestion?.rubricApplied &&
      (suggestion?.standard || suggestion?.standards?.length)
  )

/**
 * Whether a question already has an attached rubric (skip for apply).
 * @param {Object} [question]
 * @returns {boolean}
 */
export const questionHasAttachedRubric = (question = {}) =>
  Boolean(question?.rubrics?._id || question?.rubrics)

/**
 * Merge drawer-selected rubric onto a metadata suggestion (essay-only).
 * Keeps rubric in Redux suggestion state like standards/DoK — not on the item
 * until Save — so Item Preview lower metadata does not show it early.
 * @param {Object} params
 * @param {Object} params.suggestion — API metadata suggestion
 * @param {Object} [params.question] — question (type + existing rubrics)
 * @param {{ metadata?: Object, maxScore?: number }|null} [params.selectedRubric]
 * @returns {Object}
 */
export const withSelectedRubricOnSuggestion = ({
  suggestion = {},
  question = null,
  selectedRubric = null,
}) => {
  if (!selectedRubric?.metadata) return suggestion
  if (!(suggestion?.standard || suggestion?.standards?.length)) {
    return suggestion
  }
  if (!PEAR_ASSIST_RUBRIC_ESSAY_TYPES.includes(question?.type)) {
    return suggestion
  }
  if (questionHasAttachedRubric(question)) return suggestion

  const { metadata, maxScore } = selectedRubric
  return {
    ...suggestion,
    rubricApplied: true,
    rubricId: metadata._id,
    rubricName: metadata.name || 'View rubric',
    rubricMaxScore: maxScore,
    rubrics: metadata,
  }
}

/**
 * Whether a single sub-question is eligible for CON-756 rubric apply.
 * Editable or clonable items (same as Pear Assist standards); skips questions
 * that already have rubrics. Clonable items are copied on save via
 * `cloneNonOwnedItems`.
 * @param {Object} params
 * @param {boolean} [params.requireMissingStandards] — when true, skip questions
 *   that already have standards (drawer enablement / stamp targets)
 * @returns {boolean}
 */
export const isQuestionEligibleForRubricApply = ({
  item,
  question,
  userId,
  writableCollections = [],
  userCollections = [],
  userRole,
  isCurator,
  requireMissingStandards = false,
}) =>
  Boolean(question) &&
  PEAR_ASSIST_RUBRIC_ESSAY_TYPES.includes(question.type) &&
  !questionHasAttachedRubric(question) &&
  (!requireMissingStandards || !isStandardAddedToQuestion(question)) &&
  canApplyRubricToItem({
    item,
    userId,
    writableCollections,
    userCollections,
    userRole,
    isCurator,
  })

/**
 * Eligible essay targets for Pear Assist rubric apply across test items.
 * @param {Object} params
 * @param {boolean} [params.requireMissingStandards] — drawer / stamp path only
 * @returns {{ targets: Array<{ itemId: string, questionId: string, question: Object }>, skippedWithRubricCount: number }}
 */
export const getEligibleRubricApplyTargets = ({
  testItems = [],
  userId,
  writableCollections = [],
  userCollections = [],
  userRole,
  isCurator,
  requireMissingStandards = false,
}) => {
  let skippedWithRubricCount = 0
  const targets = []

  ;(testItems || []).forEach((item) => {
    const questions = item?.data?.questions || []
    questions.forEach((question) => {
      if (!PEAR_ASSIST_RUBRIC_ESSAY_TYPES.includes(question?.type)) {
        return
      }
      if (questionHasAttachedRubric(question)) {
        skippedWithRubricCount += 1
        return
      }
      if (requireMissingStandards && isStandardAddedToQuestion(question)) {
        return
      }
      if (
        !canApplyRubricToItem({
          item,
          userId,
          writableCollections,
          userCollections,
          userRole,
          isCurator,
        })
      ) {
        return
      }
      targets.push({
        itemId: item._id,
        questionId: question.id,
        question,
      })
    })
  })

  return { targets, skippedWithRubricCount }
}

/**
 * Load rubrics for the Pear Assist dropdown via existing `POST /rubrics/search`
 * (`rubricsApi.getSerchedRubrics`). Only rubrics owned by the current user
 * (and not archived) are listed.
 * @param {string} userId — current user; required for ownership filter
 * @param {{ searchString?: string, page?: number, limit?: number, throwOnError?: boolean }} [opts]
 * @returns {Promise<Array<{ value: string, label: string, rubric: Object }>>}
 */
export const fetchRubricsForDropdown = async (
  userId,
  { searchString = '', page = 1, limit = 50, throwOnError = false } = {}
) => {
  if (!userId) return []
  const ownerId = `${userId}`
  try {
    const result = await rubricsApi.getSerchedRubrics({
      limit,
      page,
      searchString,
    })
    // Empty searchString returns a Mongo array; search hits return `{ rubrics, total }`.
    const rubrics = Array.isArray(result) ? result : result?.rubrics || []
    return rubrics
      .filter(
        (rubric) =>
          rubric?.status !== 'archived' &&
          `${rubric?.createdBy?._id}` === ownerId
      )
      .map((rubric) => ({
        value: rubric._id,
        label: rubric.name,
        rubric,
      }))
  } catch (err) {
    // Soft failure for drawer: empty options; caller may notify via throwOnError.
    if (throwOnError) throw err
    return []
  }
}

/**
 * Ensure full rubric (with criteria) for apply; return metadata + maxScore.
 * @param {Object|string} rubricOrId — full rubric object or id
 * @returns {Promise<{ rubric: Object, metadata: Object, maxScore: number }>}
 */
export const resolveRubricForApply = async (rubricOrId) => {
  const id =
    typeof rubricOrId === 'object' && rubricOrId !== null
      ? rubricOrId._id
      : rubricOrId
  let rubric =
    typeof rubricOrId === 'object' && rubricOrId !== null ? rubricOrId : null

  if (!rubric?.criteria?.length) {
    const fetched = await rubricsApi.getRubricsById(id)
    // GET /rubrics/:id returns a Mongo array from getByFields.
    rubric = Array.isArray(fetched) ? fetched[0] : fetched
  }

  if (!rubric?.criteria?.length) {
    const error = new Error('Rubric criteria missing')
    error.code = 'RUBRIC_CRITERIA_MISSING'
    throw error
  }

  return {
    rubric,
    metadata: buildRubricAttachMetadata(rubric),
    maxScore: computeRubricMaxScore(rubric),
  }
}

/**
 * CON-756 Review browse modal: set/replace rubric on the Pear Assist suggestion
 * for one question (suggestion-only until Save — same as standards).
 */
export const applyPearAssistRubricToQuestion = async ({
  rubricOrId,
  itemId,
  questionId,
  suggestionsByQuestionId = {},
}) => {
  if (!itemId || !questionId) {
    return { applied: false, seededSuggestions: {} }
  }
  const { metadata, maxScore } = await resolveRubricForApply(rubricOrId)

  return {
    applied: true,
    metadata,
    seededSuggestions: {
      [questionId]: {
        ...(suggestionsByQuestionId?.[questionId] || {}),
        rubricApplied: true,
        rubricId: metadata?._id,
        rubricName: metadata?.name || 'View rubric',
        rubricMaxScore: maxScore,
        rubrics: metadata,
      },
    },
  }
}

/**
 * CON-756 Review browse modal: clear Pear Assist rubric from the suggestion
 * for one question (suggestion-only until Save).
 */
export const removePearAssistRubricFromQuestion = ({
  itemId,
  questionId,
  suggestionsByQuestionId = {},
}) => {
  if (!itemId || !questionId) {
    return { removed: false, seededSuggestions: {} }
  }
  const prior = suggestionsByQuestionId?.[questionId] || {}
  const nextSuggestion = { ...prior }
  delete nextSuggestion.rubricApplied
  delete nextSuggestion.rubricId
  delete nextSuggestion.rubricName
  delete nextSuggestion.rubricMaxScore
  delete nextSuggestion.rubrics

  return {
    removed: true,
    seededSuggestions: {
      [questionId]: nextSuggestion,
    },
  }
}

export const unwrapRubric = (fetched) => {
  if (Array.isArray(fetched)) return fetched[0] || null
  return fetched || null
}

export const findQuestionInTestItems = (testItems, itemId, questionId) => {
  if (!itemId || !questionId) return null
  for (const item of testItems || []) {
    if (`${item?._id}` !== `${itemId}`) continue
    return (
      (item?.data?.questions || []).find((q) => q?.id === questionId) || null
    )
  }
  return null
}

/**
 * First Pear Assist–applied rubric on an item that should show Review VIEW
 * (rubricApplied + generated standards).
 */
export const getAppliedRubricForItem = (item, suggestions = {}) => {
  const questions = item?.data?.questions || []

  for (const q of questions) {
    const suggestion = suggestions?.[q?.id]
    if (!shouldShowPearAssistRubric(suggestion)) continue
    const rubricId = suggestion.rubricId || null
    if (!rubricId) continue
    return {
      itemId: item?._id,
      questionId: q.id,
      rubricId,
      rubricName: suggestion.rubricName || 'View rubric',
    }
  }

  return null
}
